import { formatDistanceToNow } from 'date-fns';
import { es } from 'date-fns/locale';
import { Mail, Users, Send } from 'lucide-react';
import { Skeleton } from '@/components/ui/skeleton';
import type { ActiveCampaignStats as ACStats } from '@/hooks/useActiveCampaignStats';

interface ActiveCampaignStatsProps {
  stats: ACStats | undefined;
  isLoading: boolean;
}

function StatItem({
  icon: Icon,
  label,
  value,
  subtext
}: {
  icon: React.ElementType;
  label: string;
  value: string | number;
  subtext?: string;
}) {
  return (
    <div className="space-y-2">
      <div className="flex items-center gap-2 text-sm text-muted-foreground">
        <Icon className="w-4 h-4" />
        <span>{label}</span>
      </div>
      <p className="text-2xl font-bold">{value}</p>
      {subtext && (
        <p className="text-xs text-muted-foreground">{subtext}</p>
      )}
    </div>
  );
}

function StatsSkeleton() {
  return (
    <div className="grid gap-6 md:grid-cols-3">
      {[...Array(3)].map((_, i) => (
        <div key={i} className="space-y-2">
          <Skeleton className="h-4 w-24" />
          <Skeleton className="h-8 w-16" />
          <Skeleton className="h-3 w-32" />
        </div>
      ))}
    </div>
  );
}

function EmptyState() {
  return (
    <div className="flex flex-col items-center justify-center py-8 text-center">
      <div className="w-12 h-12 rounded-full bg-muted flex items-center justify-center mb-3">
        <Mail className="w-6 h-6 text-muted-foreground" />
      </div>
      <p className="text-sm font-medium">Sin datos de email</p>
      <p className="text-xs text-muted-foreground mt-1">
        Los datos aparecerán cuando se envíen emails de reporte
      </p>
    </div>
  );
}

export function ActiveCampaignStats({ stats, isLoading }: ActiveCampaignStatsProps) {
  if (isLoading) {
    return <StatsSkeleton />;
  }

  if (!stats || stats.emailsSent === 0) {
    return <EmptyState />;
  }

  const lastEmailText = stats.lastEmailSent
    ? formatDistanceToNow(new Date(stats.lastEmailSent), {
        addSuffix: true,
        locale: es
      })
    : 'Nunca';

  return (
    <div className="grid gap-6 md:grid-cols-3">
      <StatItem
        icon={Send}
        label="Emails Enviados"
        value={stats.emailsSent}
        subtext="Total de reportes enviados"
      />

      <StatItem
        icon={Users}
        label="Contactos Activos"
        value={stats.activeContacts}
        subtext="Últimos 30 días"
      />

      <StatItem
        icon={Mail}
        label="Último Envío"
        value={lastEmailText}
        subtext="Último email de reporte"
      />
    </div>
  );
}
